/**
 * Calendar Page - Display tasks by due date
 */
import { useEffect, useMemo, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { 
  fetchTasks, 
  fetchTask, 
  updateTask,
  setTasks,
} from '@/store/taskSlice';
import { TaskModal } from '@/components/TaskModal/TaskModal';
import { Task } from '@/types';
import { Search, Filter, ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { projectMemberAPI } from '@/api/projectMemberAPI';
import { userAPI } from '@/api/userAPI';
import { CommentCreateData, Comment } from '@/types/comments';
import { commentAPI } from '@/api/commentAPI';
import { deleteComment, updateComment } from '@/utils/commentUtils';
import { Loader } from '@/components/Loader';
import toast from 'react-hot-toast';
import { mastersAPI, WorkflowStatus, TaskType, Priority } from '@/api/mastersApi';
import FilterDrawer from '@/components/FilterDrawer';
import AssigneeChips from '@/components/AssigneeChips';
import { taskAPI } from '@/api/taskAPI';
import { ensureHtml, toPlainText } from '@/utils/richText';

export const Calendar = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const dispatch = useAppDispatch();
  const { 
    tasks, 
    loading, 
    currentTask,
  } = useAppSelector((state) => state.tasks);
  const { currentProject } = useAppSelector((state) => state.projects);
  const { user: authUser } = useAppSelector((state) => state.auth);

  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState<string[]>([]);
  const [memberChips, setMemberChips] = useState<{ id: string; initials: string; color: string; name: string }[]>([]);
  const [projectMembers, setProjectMembers] = useState<any[]>([]);
  const projectKey = currentProject?.key || 'AHM';

  // Calendar state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'day'>('month');

  // Filter drawer state
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterBtnWrapRef = useRef<HTMLDivElement | null>(null);

  // Master data for filters
  const [workflowStatuses, setWorkflowStatuses] = useState<WorkflowStatus[]>([]);
  const [useFallback, setUseFallback] = useState<boolean>(false);
  const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
  const [priorities, setPriorities] = useState<Priority[]>([]);

  // Selected filters
  const [selectedStatusIds, setSelectedStatusIds] = useState<string[]>([]);
  const [selectedPriorityIds, setSelectedPriorityIds] = useState<string[]>([]);
  const [selectedTaskTypeIds, setSelectedTaskTypeIds] = useState<string[]>([]);
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);

  // Dynamic data states for modal
  const [relatedWork, setRelatedWork] = useState<any>({ blocks: [], relatesTo: [] });
  const [activity, setActivity] = useState<any[]>([]);
  const [bootLoading, setBootLoading] = useState(true);
  const [mastersLoaded, setMastersLoaded] = useState(false);
  const [membersLoaded, setMembersLoaded] = useState(false);

  // Calendar date range state
  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });
  
  // Date picker state
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerValue, setDatePickerValue] = useState('');

  useEffect(() => {
    if (!projectId) return;
    
    // Calculate date range based on view mode
    let start: Date;
    let end: Date;
    
    if (viewMode === 'month') {
      const { year, month } = getDaysInMonth(currentDate);
      start = new Date(year, month, 1);
      end = new Date(year, month + 1, 0); // Last day of month
    } else if (viewMode === 'week') {
      const startOfWeek = new Date(currentDate);
      const day = startOfWeek.getDay();
      const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Monday
      startOfWeek.setDate(diff);
      start = startOfWeek;
      end = new Date(startOfWeek);
      end.setDate(startOfWeek.getDate() + 6); // Sunday
    } else { // day
      start = new Date(currentDate);
      start.setHours(0, 0, 0, 0);
      end = new Date(currentDate);
      end.setHours(23, 59, 59, 999);
    }
    
    setDateRange({ start, end });
    
    // Fetch tasks using calendar API with date range
    const startDateStr = start.toISOString().split('T')[0];
    const endDateStr = end.toISOString().split('T')[0];
    
    // Use calendar-specific API for optimized queries
    (async () => {
      try {
        setBootLoading(true);
        const calendarTasks = await taskAPI.getCalendarTasks({
          project_id: projectId,
          start_date: startDateStr,
          end_date: endDateStr,
          assigned_to: selectedAssigneeIds.length === 1 ? selectedAssigneeIds[0] : undefined,
          workflow_status_id: selectedStatusIds.length === 1 ? selectedStatusIds[0] : undefined,
          priority_id: selectedPriorityIds.length === 1 ? selectedPriorityIds[0] : undefined,
          task_type_id: selectedTaskTypeIds.length === 1 ? selectedTaskTypeIds[0] : undefined,
        });
        
        // Update tasks in Redux store using setTasks action
        dispatch(setTasks(calendarTasks));
        setBootLoading(false);
      } catch (error) {
        console.error('Failed to fetch calendar tasks:', error);
        // Fallback to regular tasks fetch
        dispatch(fetchTasks({ project_id: projectId }));
        setBootLoading(false);
      }
    })();
  }, [dispatch, projectId, currentDate, viewMode, selectedAssigneeIds, selectedStatusIds, selectedPriorityIds, selectedTaskTypeIds]);

  // Load filter masters
  useEffect(() => {
    (async () => {
      try {
        const [ws, types, prios] = await Promise.all([
          mastersAPI.getWorkflowStatuses(),
          mastersAPI.getTaskTypes(),
          mastersAPI.getPriorities(),
        ]);
        if (!ws || ws.length === 0) {
          setUseFallback(true);
          setWorkflowStatuses([]);
        } else {
          setUseFallback(false);
          setWorkflowStatuses(ws.sort((a, b) => (a.order || 0) - (b.order || 0)));
        }
        setTaskTypes(types || []);
        setPriorities(prios || []);
        setMastersLoaded(true);
      } catch {
        setUseFallback(true);
        setWorkflowStatuses([]);
        setMastersLoaded(true);
      }
    })();
  }, []);

  const normalizeMemberId = (member: any) => {
    const raw = member?.user_id ?? member?.user?.id ?? member?._id ?? member?.id;
    return raw ? String(raw) : '';
  };

  // Load project members
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!currentProject?.id) return;
      try {
        const data = await projectMemberAPI.getProjectMembers(currentProject.id);
        if (!mounted) return;
        
        setProjectMembers(data);
        
        const colorFromSeed = (seed: string) => {
          const colors = ['bg-blue-500','bg-green-500','bg-purple-500','bg-orange-500','bg-pink-500','bg-indigo-500','bg-teal-500','bg-red-500','bg-yellow-500','bg-cyan-500'];
          const sum = Array.from(seed).reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
          return colors[sum % colors.length];
        };
        
        const buildChip = (m: any) => {
          const normalizedId = normalizeMemberId(m);
          const name = m.user_display_name || m.username || m.user_email || 'User';
          const initials = (() => {
            const parts = name.trim().split(/\s+/);
            if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
            return (parts[0][0] + parts[1][0]).toUpperCase();
          })();
          const id = normalizedId || name;
          return { id, initials, color: colorFromSeed(normalizedId || name), name };
        };

        const dedupeChips = (list: typeof memberChips) => {
          const unique = new Map<string, typeof list[number]>();
          list.forEach((chip) => {
            if (!chip.id) return;
            if (!unique.has(chip.id)) unique.set(chip.id, chip);
          });
          return Array.from(unique.values());
        };

        let chips: typeof memberChips = data.map(buildChip);
        chips = dedupeChips(chips);
        
        if ((!chips || chips.length === 0) && authUser?.organization_id) {
          try {
            const orgUsers = await userAPI.getUsers({ organization_id: authUser.organization_id, is_active: true, limit: 100 });
            chips = orgUsers.map((u: any) => {
              const name = u.display_name || `${u.first_name} ${u.last_name}`.trim() || u.username || u.email || 'User';
              const initials = (() => {
                const parts = name.trim().split(/\s+/);
                if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
                return (parts[0][0] + parts[1][0]).toUpperCase();
              })();
              const id = String(u.id);
              return { id, initials, color: colorFromSeed(id || name), name };
            });
            chips = dedupeChips(chips);
          } catch {}
        }
        setMemberChips(chips);
        setMembersLoaded(true);
      } catch {}
    })();
    return () => { mounted = false; };
  }, [currentProject?.id, authUser?.organization_id]);

  // Aggregate boot loading
  useEffect(() => {
    if (!loading && mastersLoaded && membersLoaded) {
      setBootLoading(false);
    } else {
      setBootLoading(true);
    }
  }, [loading, mastersLoaded, membersLoaded]);

  // Handle task click to open modal
  const handleTaskClick = async (task: Task) => {
    setSelectedTask(task);
    setIsTaskModalOpen(true);
    
    try {
      await Promise.all([
        dispatch(fetchTask(task.id)),
      ]);
      
      const [relatedWorkData, activityData] = await Promise.all([
        fetchRelatedWork(task.id),
        fetchTaskActivity(task.id)
      ]);
      
      setRelatedWork(relatedWorkData);
      setActivity(activityData);
    } catch (error) {
      console.error('Failed to fetch task details:', error);
    }
  };

  // Handle modal close
  const handleTaskModalClose = () => {
    setIsTaskModalOpen(false);
    setSelectedTask(null);
    setRelatedWork({ blocks: [], relatesTo: [] });
    setActivity([]);
  };

  // API handlers
  const handleStatusUpdate = async (taskId: string, newStatus: string) => {
    try {
      await dispatch(updateTask({
        id: taskId,
        data: { status: newStatus as any }
      })).unwrap();
      
      if (projectId) {
        dispatch(fetchTasks({ project_id: projectId }));
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleAddComment = async (taskId: string, html: string, parentId?: string, plainText?: string) => {
    try {
      const newComment = await addComment(taskId, html, parentId, plainText);
      const activityData = await fetchTaskActivity(taskId);
      setActivity(activityData);
      return newComment;
    } catch (error) {
      console.error('Failed to add comment:', error);
      throw error;
    }
  };

  const handleUpdateDescription = async (taskId: string, description: string) => {
    try {
      await dispatch(updateTask({
        id: taskId,
        data: { description }
      })).unwrap();
      
      dispatch(fetchTask(taskId));
    } catch (error) {
      console.error('Failed to update description:', error);
      throw error;
    }
  };

  const handleUpdateAssignee = async (taskId: string, assigneeId: string | null) => {
    try {
      await dispatch(updateTask({
        id: taskId,
        data: { assigned_to: assigneeId === null ? null : assigneeId }
      })).unwrap();
      
      if (projectId) {
        dispatch(fetchTasks({ project_id: projectId }));
      }
      dispatch(fetchTask(taskId));
    } catch (error) {
      console.error('Failed to update assignee:', error);
      throw error;
    }
  };

  const handleUpdateReporter = async (taskId: string, reporterId: string | null) => {
    try {
      await dispatch(updateTask({
        id: taskId,
        data: { reporter_id: reporterId === null ? null : reporterId }
      })).unwrap();
      
      if (projectId) {
        dispatch(fetchTasks({ project_id: projectId }));
      }
      dispatch(fetchTask(taskId));
    } catch (error) {
      console.error('Failed to update reporter:', error);
      throw error;
    }
  };

  const handleUpdateComment = async (commentId: string, html: string, plainText?: string) => {
    try {
      await updateComment(commentId, html, plainText);
      if (selectedTask?.id) {
        const activityData = await fetchTaskActivity(selectedTask.id);
        setActivity(activityData);
      }
    } catch (error) {
      console.error('Failed to update comment:', error);
      throw error;
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await deleteComment(commentId);
      if (selectedTask?.id) {
        const activityData = await fetchTaskActivity(selectedTask.id);
        setActivity(activityData);
      }
    } catch (error) {
      console.error('Failed to delete comment:', error);
      throw error;
    }
  };

  const handleUpdateTitle = async (id: string, newTitle: string) => {
    if (!selectedTask) return;
    
    try {
      await dispatch(updateTask({
        id: id || selectedTask.id,
        data: { title: newTitle }
      })).unwrap();
      const taskId = id || currentTask?.id || selectedTask.id;
      dispatch(fetchTask(taskId));
    } catch (error) {
      console.error('Failed to update title:', error);
      toast.error('Failed to update title');
      throw error;
    }
  };

  const handleAssigneeToggle = (userId: string) => {
    setSelectedAssigneeIds(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  // Unique labels for Labels tab
  const allLabels = useMemo(() => {
    const set = new Set<string>();
    tasks.forEach((t: any) => {
      const labels = Array.isArray(t?.labels)
        ? t.labels
        : typeof t?.labels === 'string'
          ? (t.labels as string).split(',').map((l) => l.trim()).filter(Boolean)
          : [];
      labels.forEach((l: string) => set.add(l));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [tasks]);

  const effectiveStatuses: WorkflowStatus[] = useMemo(() => {
    if (!useFallback) return workflowStatuses;
    return [
      { id: 'todo', name: 'TO DO', order: 1 },
      { id: 'requirements', name: 'REQUIREMENTS SIGN-OFF', order: 2 },
      { id: 'in_progress', name: 'DEV IN PROGRESS', order: 3 },
      { id: 'completed', name: 'DEV COMPLETED', order: 4 },
      { id: 'qa', name: 'QA IN PROGRESS', order: 5 },
    ];
  }, [workflowStatuses, useFallback]);

  const activeFilterCount = useMemo(() => {
    return selectedStatusIds.length + selectedPriorityIds.length + selectedTaskTypeIds.length + selectedAssigneeIds.length + selectedLabels.length;
  }, [selectedStatusIds.length, selectedPriorityIds.length, selectedTaskTypeIds.length, selectedAssigneeIds.length, selectedLabels.length]);

  // Filter tasks
  const filteredTasks = useMemo(() => {
    const byId = (v?: string) => (v ? String(v) : '');
    return tasks.filter((t: any) => {
      const matchesSearch = (t.title || '').toLowerCase().includes(searchQuery.toLowerCase());
      if (!matchesSearch) return false;

      if (selectedAssigneeIds.length > 0 && !selectedAssigneeIds.includes(byId(t.assigned_to))) return false;
      if (selectedPriorityIds.length > 0 && !selectedPriorityIds.includes(byId(t.priority_id))) return false;
      if (selectedTaskTypeIds.length > 0 && !selectedTaskTypeIds.includes(byId(t.task_type_id))) return false;

      if (selectedStatusIds.length > 0) {
        const sid = byId((t as any).workflow_status_id);
        if (sid) {
          if (!selectedStatusIds.includes(sid)) return false;
        } else if ((t as any).status) {
          const legacy = String((t as any).status).toLowerCase();
          const nameMatch = effectiveStatuses.find((s) => selectedStatusIds.includes(s.id) && (s.name || '').toLowerCase().includes(legacy));
          if (!nameMatch) return false;
        } else {
          return false;
        }
      }

      if (selectedLabels.length > 0) {
        const labelsArr = Array.isArray(t?.labels)
          ? t.labels
          : typeof t?.labels === 'string'
            ? (t.labels as string).split(',').map((l) => l.trim()).filter(Boolean)
            : [];
        const hasAny = labelsArr.some((l: string) => selectedLabels.includes(l));
        if (!hasAny) return false;
      }

      return true;
    });
  }, [tasks, searchQuery, selectedAssigneeIds, selectedPriorityIds, selectedTaskTypeIds, selectedStatusIds, selectedLabels, effectiveStatuses]);

  // Calendar helpers
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  // Get tasks for a specific date
  const getTasksForDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return filteredTasks.filter((task: any) => {
      if (!task.due_date) return false;
      const taskDate = new Date(task.due_date);
      const taskDateStr = taskDate.toISOString().split('T')[0];
      return taskDateStr === dateStr;
    });
  };

  // Navigate months
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    if (viewMode === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    } else if (viewMode === 'week') {
      const nextWeek = new Date(currentDate);
      nextWeek.setDate(nextWeek.getDate() + 7);
      setCurrentDate(nextWeek);
    } else if (viewMode === 'day') {
      const nextDay = new Date(currentDate);
      nextDay.setDate(nextDay.getDate() + 1);
      setCurrentDate(nextDay);
    }
  };

  const goToPreviousWeek = () => {
    if (viewMode === 'week') {
      const prevWeek = new Date(currentDate);
      prevWeek.setDate(prevWeek.getDate() - 7);
      setCurrentDate(prevWeek);
    } else if (viewMode === 'day') {
      const prevDay = new Date(currentDate);
      prevDay.setDate(prevDay.getDate() - 1);
      setCurrentDate(prevDay);
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setShowDatePicker(false);
  };

  const handleDatePickerChange = (dateStr: string) => {
    if (dateStr) {
      const selectedDate = new Date(dateStr);
      if (!isNaN(selectedDate.getTime())) {
        setCurrentDate(selectedDate);
        setShowDatePicker(false);
      }
    }
  };

  // Prepare task details for the modal
  const getTaskDetails = () => {
    if (!currentTask) return {};
    
    const rawLabels: string | string[] | null | undefined = (currentTask as any)?.labels;
    return {
      assignee: currentTask.assigned_to || 'Unassigned',
      labels: Array.isArray(rawLabels)
        ? rawLabels
        : typeof rawLabels === "string"
          ? rawLabels.split(",").map((label: string) => label.trim())
          : [],
      dueDate: currentTask.due_date || 'Not set',
      parent: (currentTask as any).parent_id || 'None',
      team: 'Development Team',
      testTeam: 'QA Team',
      sme: [],
      sprint: currentTask.sprint_id || 'Not set',
      originalEstimate: (currentTask as any).estimate || 'Not set',
      timeRemaining: 'Not set',
      development: 'In Progress',
      reporter: 'Janarthanan',
      organization: 'Health Individual'
    };
  };

  if (bootLoading) {
    return <Loader variant="overlay" size="xl" text="Loading calendar..." />;
  }

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const today = new Date();
  const isToday = (date: Date) => {
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  // Generate calendar days
  const calendarDays = [];
  // Add empty cells for days before the first day of the month
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(null);
  }
  // Add days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(new Date(year, month, day));
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      {/* Calendar Header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                className="w-64 h-8 pl-10 pr-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="Search calendar"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <AssigneeChips 
              items={memberChips.map(u => ({ id: u.id, name: u.name }))} 
              selectedIds={selectedAssigneeIds} 
              onToggle={handleAssigneeToggle} 
            />
            <div ref={filterBtnWrapRef} className="relative">
              <button 
                onClick={() => setIsFilterOpen((v) => !v)} 
                className="h-8 px-3 rounded-md border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 flex items-center gap-2 relative"
              >
                <Filter className="w-4 h-4" />
                Filter
                {activeFilterCount > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center text-[10px] leading-none font-semibold bg-blue-600 text-white rounded-full px-1.5 py-0.5">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              {isFilterOpen && (
                <FilterDrawer
                  isOpen={isFilterOpen}
                  anchorRef={filterBtnWrapRef}
                  onClose={() => setIsFilterOpen(false)}
                  workflowStatuses={effectiveStatuses}
                  taskTypes={taskTypes}
                  priorities={priorities}
                  sprints={[]}
                  members={projectMembers as any}
                  allLabels={allLabels}
                  selectedStatusIds={selectedStatusIds} 
                  setSelectedStatusIds={setSelectedStatusIds}
                  selectedPriorityIds={selectedPriorityIds} 
                  setSelectedPriorityIds={setSelectedPriorityIds}
                  selectedTaskTypeIds={selectedTaskTypeIds} 
                  setSelectedTaskTypeIds={setSelectedTaskTypeIds}
                  selectedSprintIds={[]} 
                  setSelectedSprintIds={() => {}}
                  selectedAssigneeIds={selectedAssigneeIds} 
                  setSelectedAssigneeIds={setSelectedAssigneeIds}
                  selectedLabels={selectedLabels} 
                  setSelectedLabels={setSelectedLabels}
                  parentOnly={false} 
                  setParentOnly={() => {}}
                  subtaskOnly={false} 
                  setSubtaskOnly={() => {}}
                />
              )}
            </div>
          </div>
          
          {/* Calendar Navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={goToToday}
              className="h-8 px-3 rounded-md border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50"
            >
              Today
            </button>
            <div className="flex items-center gap-1">
              <button
                onClick={viewMode === 'month' ? goToPreviousMonth : goToPreviousWeek}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
               <span className="text-sm font-semibold text-gray-900 min-w-[120px] text-center">
              {viewMode === 'month' ? `${monthNames[month]} ${year}` : 
               viewMode === 'week' ? (() => {
                 const startOfWeek = new Date(currentDate);
                 const day = startOfWeek.getDay();
                 const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
                 startOfWeek.setDate(diff);
                 const endOfWeek = new Date(startOfWeek);
                 endOfWeek.setDate(startOfWeek.getDate() + 6);
                 return `${startOfWeek.getDate()} - ${endOfWeek.getDate()} ${monthNames[endOfWeek.getMonth()]}, ${endOfWeek.getFullYear()}`;
               })() :
               `${dayNames[currentDate.getDay()]}, ${monthNames[currentDate.getMonth()]} ${currentDate.getDate()}, ${currentDate.getFullYear()}`}
            </span>
              <button
                onClick={goToNextMonth}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>
            </div>
           
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value as 'month' | 'week' | 'day')}
              className="h-8 px-3 rounded-md border border-gray-300 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="month">Month</option>
              <option value="week">Week</option>
              <option value="day">Day</option>
            </select>
            <div className="relative" data-date-picker>
              <button 
                onClick={() => {
                  setShowDatePicker(!showDatePicker);
                  setDatePickerValue(currentDate.toISOString().split('T')[0]);
                }}
                className="p-2 hover:bg-gray-100 rounded"
                title="Go to date"
              >
                <CalendarIcon className="w-4 h-4 text-gray-600" />
              </button>
              {showDatePicker && (
                <div className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-3 z-50 min-w-[240px]">
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-medium text-gray-700">Go to date</label>
                    <input
                      type="date"
                      value={datePickerValue}
                      onChange={(e) => {
                        setDatePickerValue(e.target.value);
                        if (e.target.value) {
                          handleDatePickerChange(e.target.value);
                        }
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                    <div className="flex gap-2 mt-1">
                      <button
                        onClick={goToToday}
                        className="flex-1 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded border border-blue-200"
                      >
                        Today
                      </button>
                      <button
                        onClick={() => setShowDatePicker(false)}
                        className="flex-1 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 rounded border border-gray-200"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="flex-1 overflow-auto p-4 bg-gray-50">
        {viewMode === 'month' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Day Headers */}
            <div className="grid grid-cols-7 border-b border-gray-200">
              {dayNames.map((day) => (
                <div key={day} className="p-2 text-center text-xs font-semibold text-gray-600 border-r border-gray-200 last:border-r-0">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar Days */}
            <div className="grid grid-cols-7">
              {calendarDays.map((date, index) => {
                if (!date) {
                  return <div key={`empty-${index}`} className="min-h-[100px] border-r border-b border-gray-200 last:border-r-0" />;
                }
                
                const dayTasks = getTasksForDate(date);
                const isCurrentDay = isToday(date);
                
                return (
                  <div
                    key={date.toISOString()}
                    className={`min-h-[100px] border-r border-b border-gray-200 last:border-r-0 p-2 ${
                      isCurrentDay ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className={`text-sm font-medium mb-1 ${isCurrentDay ? 'text-blue-600' : 'text-gray-900'}`}>
                      {date.getDate()}
                    </div>
                    <div className="space-y-1">
                      {dayTasks.slice(0, 3).map((task: any) => {
                        const statusColor = (task as any).priority === 'high' 
                          ? 'bg-red-100 text-red-800' 
                          : (task as any).priority === 'medium'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800';
                        
                        return (
                          <div
                            key={task.id}
                            onClick={() => handleTaskClick(task)}
                            className={`text-xs p-1 rounded cursor-pointer hover:opacity-80 ${statusColor} truncate`}
                            title={task.title}
                          >
                            {task.title}
                          </div>
                        );
                      })}
                      {dayTasks.length > 3 && (
                        <div className="text-xs text-gray-500">
                          +{dayTasks.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {viewMode === 'week' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Day Headers */}
            <div className="grid grid-cols-7 border-b border-gray-200">
              {(() => {
                const startOfWeek = new Date(currentDate);
                const day = startOfWeek.getDay();
                const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday start
                startOfWeek.setDate(diff);
                
                const weekDays = [];
                for (let i = 0; i < 7; i++) {
                  const date = new Date(startOfWeek);
                  date.setDate(startOfWeek.getDate() + i);
                  weekDays.push(date);
                }
                return weekDays.map((date) => {
                  const isCurrentDay = isToday(date);
                  return (
                    <div 
                      key={date.toISOString()} 
                      className={`p-2 text-center border-r border-gray-200 last:border-r-0 ${isCurrentDay ? 'bg-blue-50' : ''}`}
                    >
                      <div className={`text-xs font-semibold ${isCurrentDay ? 'text-blue-600' : 'text-gray-600'}`}>
                        {dayNames[date.getDay()]}
                      </div>
                      <div className={`text-sm font-medium ${isCurrentDay ? 'text-blue-600' : 'text-gray-900'}`}>
                        {date.getDate()}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
            
            {/* Week Days */}
            <div className="grid grid-cols-7">
              {(() => {
                const startOfWeek = new Date(currentDate);
                const day = startOfWeek.getDay();
                const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday start
                startOfWeek.setDate(diff);
                
                const weekDays = [];
                for (let i = 0; i < 7; i++) {
                  const date = new Date(startOfWeek);
                  date.setDate(startOfWeek.getDate() + i);
                  weekDays.push(date);
                }
                
                return weekDays.map((date) => {
                  const dayTasks = getTasksForDate(date);
                  const isCurrentDay = isToday(date);
                  
                  return (
                    <div
                      key={date.toISOString()}
                      className={`min-h-[500px] border-r border-gray-200 last:border-r-0 p-2 ${
                        isCurrentDay ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="space-y-2">
                        {dayTasks.map((task: any) => {
                          const statusColor = (task as any).priority === 'high' 
                            ? 'bg-red-100 text-red-800 border-red-300' 
                            : (task as any).priority === 'medium'
                            ? 'bg-yellow-100 text-yellow-800 border-yellow-300'
                            : 'bg-green-100 text-green-800 border-green-300';
                          
                          return (
                            <div
                              key={task.id}
                              onClick={() => handleTaskClick(task)}
                              className={`text-sm p-2 rounded cursor-pointer hover:opacity-80 ${statusColor} border-l-4`}
                              title={task.title}
                            >
                              <div className="font-medium truncate">{task.title}</div>
                              {task.due_date && (
                                <div className="text-xs opacity-75 mt-1">
                                  {new Date(task.due_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        )}

        {viewMode === 'day' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Day Header */}
            <div className="border-b border-gray-200 p-4">
              <div className={`text-lg font-semibold ${isToday(currentDate) ? 'text-blue-600' : 'text-gray-900'}`}>
                {dayNames[currentDate.getDay()]}, {monthNames[currentDate.getMonth()]} {currentDate.getDate()}, {currentDate.getFullYear()}
              </div>
            </div>
            
            {/* Day Tasks */}
            <div className="p-4">
              {(() => {
                const dayTasks = getTasksForDate(currentDate);
                
                if (dayTasks.length === 0) {
                  return (
                    <div className="text-center py-12 text-gray-500">
                      No tasks scheduled for this day
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-3">
                    {dayTasks.map((task: any) => {
                      const statusColor = (task as any).priority === 'high' 
                        ? 'bg-red-100 text-red-800 border-red-300' 
                        : (task as any).priority === 'medium'
                        ? 'bg-yellow-100 text-yellow-800 border-yellow-300'
                        : 'bg-green-100 text-green-800 border-green-300';
                      
                      return (
                        <div
                          key={task.id}
                          onClick={() => handleTaskClick(task)}
                          className={`p-4 rounded-lg cursor-pointer hover:opacity-80 ${statusColor} border-l-4 shadow-sm`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-semibold text-base mb-2">{task.title}</div>
                              {task.due_date && (
                                <div className="text-sm opacity-75">
                                  Due: {new Date(task.due_date).toLocaleString()}
                                </div>
                              )}
                              {task.description && (
                                <div className="text-sm mt-2 opacity-90 line-clamp-2">
                                  {toPlainText(ensureHtml(task.description))}
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              {task.assigned_to && (
                                <div className="text-xs opacity-75">
                                  Assigned
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      {/* Task Detail Modal */}
      <TaskModal
        isOpen={isTaskModalOpen}
        onClose={handleTaskModalClose}
        task={currentTask || selectedTask}
        onStatusUpdate={handleStatusUpdate}
        onAddComment={handleAddComment}
        onUpdateDescription={handleUpdateDescription}
        onUpdateAssignee={handleUpdateAssignee}
        onUpdateReporter={handleUpdateReporter}
        activity={activity}
        taskDetails={getTaskDetails()}
        projectMembers={projectMembers}
        comments={activity}
        onDeleteComment={handleDeleteComment}
        onUpdateComment={handleUpdateComment}
        projectKey={projectKey}
        onUpdateTaskTitle={handleUpdateTitle}
        workflowStatuses={effectiveStatuses}
        priorities={priorities}
        project={currentProject}
      />
    </div>
  );
};

// Mock API functions
const fetchRelatedWork = async (taskId: string) => {
  void taskId;
  return {
    blocks: [],
    relatesTo: []
  };
};

const fetchTaskActivity = async (taskId: string) => {
  const comments = await commentAPI.getComments(taskId);
  return comments;
};

const addComment = async (taskId: string, html: string, parentId?: string, plainText?: string) => {
  const contentHtml = html;
  const contentText = plainText ?? toPlainText(ensureHtml(html || ''));
  const commentData: CommentCreateData = {
    content: contentText,
    content_html: contentHtml,
    content_text: contentText,
    task_id: taskId,
    parent_comment_id: parentId
  };
  return await commentAPI.createComment(commentData);
};

